// src/resources/customerRfm/TagSegmentDialog.tsx
//
// "Etiquetar estes clientes" (spec §6.2 item 5): transforma o filtro atual da
// tela em etiqueta — nova ou existente. A etiquetagem roda por filtro no
// backend (não por lista de ids), então grupos grandes ficam prontos em
// background (`pending` > 0).
import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Radio, RadioGroup, TextField, Typography,
} from '@mui/material';
import { useDataProvider } from 'react-admin';
import ManualLinkButton from '../../manual/components/ManualLinkButton';
import { __t } from '../../i18n/t';
import { TagType } from '../../models';
import { RfmFilters, TagRfmResult, tagRfmCustomers } from './api';
import { RFM_MANUAL_SECTION, RfmSegment, SEGMENT_META, suggestedTagName } from './segments';

interface TagOption { id: string; name: string; color?: string | null }

interface Props {
  open: boolean;
  onClose: () => void;
  chainId: string;
  segment?: RfmSegment;
  filter: Omit<RfmFilters, 'chainId'>;
  estimatedTotal: number;
}

const TagSegmentDialog: React.FC<Props> = ({ open, onClose, chainId, segment, filter, estimatedTotal }) => {
  const dataProvider = useDataProvider();
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [name, setName] = useState('');
  const [color, setColor] = useState('#7869DF');
  const [tags, setTags] = useState<TagOption[]>([]);
  const [existingTag, setExistingTag] = useState<TagOption | null>(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<TagRfmResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setError(null);
    setMode('new');
    setExistingTag(null);
    // i18n: template literal passado direto pra setState (não é JSX/notify), o plugin de
    // build não embrulha — __t explícito no trecho variável ("Novos"), mês/ano fica em
    // pt-BR igual ao restante do nome sugerido (suggestedTagName em segments.ts).
    setName(segment ? suggestedTagName(segment) : `RFM · ${__t('Novos')} · ${new Date().toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })}`);
    setColor(segment ? SEGMENT_META[segment].color : '#7869DF');
    dataProvider
      .getList('chains/chainId/tag', {
        pagination: { page: 1, perPage: 999 },
        sort: { field: 'name', order: 'ASC' },
        filter: { type: TagType.client, chainId },
      })
      .then(({ data }: any) => setTags(data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, segment, chainId]);

  const canSubmit = mode === 'new' ? name.trim().length > 0 : !!existingTag;

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await tagRfmCustomers({
        chainId,
        tagId: mode === 'existing' ? existingTag?.id : undefined,
        tagName: mode === 'new' ? name.trim() : undefined,
        color: mode === 'new' ? color : undefined,
        filter,
      });
      setResult(res);
    } catch (err: any) {
      // i18n: fallback fora de JSX (setState), o plugin de build não embrulha.
      setError(err?.body?.message || err?.message || __t('Não foi possível etiquetar os clientes. Tente de novo.'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Etiquetar estes clientes</DialogTitle>
      <DialogContent>
        {result ? (
          <Box>
            {/* i18n: template literal dentro de ternário não é embrulhado pelo plugin de
                build — __t explícito nos dois ramos. */}
            <Alert severity="success" sx={{ mb: 1.5 }}>
              {result.pending > 0
                ? __t('Etiquetando %{total} clientes… a etiqueta "%{tagName}" fica completa em alguns minutos.', {
                    total: result.total.toLocaleString('pt-BR'), tagName: result.tagName,
                  })
                : __t('Pronto: %{taggedNow} clientes etiquetados com "%{tagName}".', {
                    taggedNow: result.taggedNow.toLocaleString('pt-BR'), tagName: result.tagName,
                  })}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Use a etiqueta em Notificações, Clube de Vantagens, Sorteios, Descontos ou na Campanha de recuperação.
            </Typography>
            <ManualLinkButton slug="dashboard-v2" section={RFM_MANUAL_SECTION} label="Onde usar a etiqueta?" compact />
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {__t('Vai etiquetar %{n} clientes com o filtro atual da tela.', { n: estimatedTotal.toLocaleString('pt-BR') })}
            </Typography>
            <RadioGroup value={mode} onChange={(_, v) => setMode(v as 'new' | 'existing')}>
              <FormControlLabel value="new" control={<Radio />} label="Nova etiqueta" />
              {mode === 'new' && (
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', ml: 4, mb: 1.5 }}>
                  <TextField size="small" label="Nome da etiqueta" value={name} onChange={(e) => setName(e.target.value)} sx={{ flex: 1 }} />
                  <TextField size="small" type="color" label="Cor" value={color} onChange={(e) => setColor(e.target.value)} sx={{ width: 90 }} />
                </Box>
              )}
              <FormControlLabel value="existing" control={<Radio />} label="Etiqueta existente" />
              {mode === 'existing' && (
                <Box sx={{ ml: 4, mb: 1 }}>
                  <Autocomplete
                    size="small"
                    options={tags}
                    value={existingTag}
                    onChange={(_, v) => setExistingTag(v)}
                    getOptionLabel={(t) => t.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderInput={(params) => <TextField {...params} label="Etiqueta" placeholder="Escolha uma etiqueta" />}
                  />
                </Box>
              )}
            </RadioGroup>
            {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button onClick={handleClose} variant="contained">Fechar</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={saving}>Cancelar</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit || saving}>
              {saving ? <CircularProgress size={18} /> : 'Etiquetar'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TagSegmentDialog;
